import { DOCUMENT, Injectable, inject } from '@angular/core';

import {
    AuthService,
    DslService,
    Logger,
    NativeAppService,
    NativeEventType,
    NavigationService,
    Page,
    UrlService,
    UserService,
    WINDOW,
} from '@frontend/vanilla/core';
import { AccountMenuDataService } from '@frontend/vanilla/shared/account-menu';
import { WorkflowService } from '@frontend/vanilla/shared/login';
import { ReplaySubject, first, firstValueFrom } from 'rxjs';

import { HeaderBarConfig } from './header-bar.client-config';

export interface HeaderBarCloseActionHandler {
    (origin: string, ...args: any[]): void | Promise<any>;
}

@Injectable({
    providedIn: 'root',
})
export class HeaderBarService {
    private readonly handlers = new Map<string, HeaderBarCloseActionHandler>();
    private readonly enabled$$ = new ReplaySubject<boolean>(1);
    private readonly disableClose$$ = new ReplaySubject<boolean>(1);
    private readonly showBackButton$$ = new ReplaySubject<boolean>(1);
    readonly enabled$ = this.enabled$$.asObservable();
    readonly disableClose$ = this.disableClose$$.asObservable();
    readonly showBackButton$ = this.showBackButton$$.asObservable();

    private readonly _doc = inject(DOCUMENT);
    private readonly accountMenuService = inject(AccountMenuDataService);
    private readonly auth = inject(AuthService);
    private readonly config = inject(HeaderBarConfig);
    private readonly dslService = inject(DslService);
    private readonly logger = inject(Logger);
    private readonly nativeAppService = inject(NativeAppService);
    private readonly navigation = inject(NavigationService);
    private readonly page = inject(Page);
    private readonly urlService = inject(UrlService);
    private readonly user = inject(UserService);
    private readonly workflow = inject(WorkflowService);
    readonly #window = inject(WINDOW);

    constructor() {
        this.config.whenReady.pipe(first()).subscribe(() => {
            this.dslService.evaluateExpression<boolean>(this.config.isEnabledCondition).subscribe((enabled: boolean) => this.enabled$$.next(enabled));
            this.dslService
                .evaluateExpression<boolean>(this.config.disableCloseCondition)
                .subscribe((disableClose) => this.disableClose$$.next(disableClose));
            this.dslService
                .evaluateExpression<boolean>(this.config.showBackButtonCondition)
                .subscribe((showBackButton) => this.showBackButton$$.next(showBackButton));
        });
    }

    registerActions() {
        this.register('goToLastKnownProduct', () => this.navigation.goToLastKnownProduct());
        this.register('logout', () => this.logout());
        this.register('finalize', () => this.workflow.finalize());
        this.register('skip', () => this.workflow.skip());
        this.register('finalizeWorkflowWithData', (data: any) => this.workflow.finalizeWithData(data));
    }

    async close() {
        if (this.accountMenuService.routerModeReturnUrl && this.user.workflowType === 0) {
            const parsedUrl = this.urlService.parse(this.accountMenuService.routerModeReturnUrl);
            if (parsedUrl.culture != this.page.lang) {
                parsedUrl.changeCulture(this.page.lang);
            }
            this.navigation.goTo(parsedUrl, { isBackNavigation: true });
            this.accountMenuService.removeReturnUrlCookie();
            return;
        }

        await firstValueFrom(this.config.whenReady);

        const workflowCloseAction = this.config.workflowCloseAction[this.user.workflowType];

        if (workflowCloseAction) {
            this.invoke(workflowCloseAction.action, workflowCloseAction.param ? [workflowCloseAction.param] : []);
            return;
        }

        if (this.user.workflowType > 0) {
            this.logger.error(`Workflow Id ${this.user.workflowType} is not mapped in header-bar configuration.`);
            this.logout();
            return;
        }

        this.navigation.goToLastKnownProduct();
    }

    back() {
        if (this.accountMenuService.routerModeReturnUrl || this._doc.referrer) {
            this.#window.history.back();
        } else {
            this.navigation.goToLastKnownProduct();
        }
    }

    sendCloseCommand() {
        if (this.nativeAppService.isNativeApp && this.nativeAppService.htcmdSchemeEnabled) {
            this.navigation.sendCloseCommand();
        }
    }

    sendCloseEvent() {
        this.nativeAppService.sendToNative({
            eventName: NativeEventType.PAGECLOSED,
            parameters: { product: this.page.product },
        });
    }

    private invoke(fname: string | undefined, args?: any[]): Promise<void> {
        args = args || [];
        let fn: HeaderBarCloseActionHandler | undefined;
        if (fname) {
            fn = this.handlers.get(fname);
        }
        const [origin, ...restArgs] = args;
        if (fn instanceof Function) {
            const result = fn.apply(null, [origin, restArgs]);
            if (result?.then) {
                return result;
            } else {
                return Promise.resolve();
            }
        }

        return Promise.reject(`Handler for header bar action ${fname} not found.`);
    }

    private logout(): Promise<void> {
        return this.auth
            .logout({ redirectAfterLogout: false })
            .then(() => this.navigation.goToLastKnownProduct({ forceReload: true }))
            .catch(() => this.navigation.goToLastKnownProduct({ forceReload: true }));
    }

    private register(name: string, fn: HeaderBarCloseActionHandler) {
        this.handlers.set(name, fn);
    }
}
